import React from 'react';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface MiniKpiProps {
  label: string;
  value: React.ReactNode;
  trend?: 'up' | 'down' | 'flat';
  delta?: string;
  className?: string;
}

const trendStyles: Record<NonNullable<MiniKpiProps['trend']>, string> = {
  up: 'text-green-600 dark:text-green-400',
  down: 'text-red-600 dark:text-red-400',
  flat: 'text-gray-500 dark:text-zinc-400',
};

const trendIcons: Record<NonNullable<MiniKpiProps['trend']>, React.ReactNode> = {
  up: <ArrowUp className="h-3 w-3" />,
  down: <ArrowDown className="h-3 w-3" />,
  flat: <Minus className="h-3 w-3" />,
};

/**
 * MiniKpi — compact KPI cell, used inline (e.g. inside HighlightPanel.vitalStats grid).
 */
export const MiniKpi: React.FC<MiniKpiProps> = ({ label, value, trend, delta, className }) => {
  return (
    <div className={cn('bg-gray-50 rounded-md p-3 min-w-0 dark:bg-zinc-800', className)}>
      <div className="text-2xs font-semibold uppercase tracking-wider text-gray-500 truncate dark:text-zinc-400">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold text-gray-900 leading-tight tabular-nums truncate dark:text-zinc-100">
        {value}
      </div>
      {(delta || trend) && (
        <div className={cn('mt-1 flex items-center gap-1 text-xs font-medium', trend && trendStyles[trend])}>
          {trend && <span className="shrink-0">{trendIcons[trend]}</span>}
          {delta && <span className="truncate">{delta}</span>}
        </div>
      )}
    </div>
  );
};

export default MiniKpi;
